import { Box, Button, Flex, FormGroup, FormLabel, Grid } from "@eworkit/iris";
import { FormikProps } from "formik";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Option } from "types/types";

import { ITestable } from "@/browser";
import { FormikError, FormikTextInput, VALIDATION_TYPES } from "@/components/Formik";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { fetchSuggestedProfileData } from "@/data/profile-data";
import { IConsultantProfileRole, IRole } from "@/data/roles";
import { IChangeable } from "@/eworkitComponents";
import { withFormikField } from "@/formikComponents";
import { useFetcher } from "@/hooks/http";
import { ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { SuggestedSection } from "@/views/components/ConsultantProfile/SuggestedData";
import { FormikCodelistGenericSelect } from "@/views/components/Formik";
import { FormikRoleCreatableTypeahead } from "@/views/components/RoleCreatableTypeahead";
import { SuggestedRolesList } from "@/views/components/Roles";
import { POSITION_MAX_CHARACTERS } from "@/views/ConsultantProfiles/Detail/GeneralSection/GeneralSectionEdit";

const ROLE_LEVELS_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "skill-role-levels" };

export interface IConsultantProfileRoleSectionProps extends IChangeable, ITestable {
  value: IConsultantProfileRole[];
  name: string;
  form: FormikProps<any>;
  showCompanyPosition?: boolean;
}

export const ConsultantProfileRoleSection: React.FC<IConsultantProfileRoleSectionProps> = React.memo(
  ({ value, form, showCompanyPosition }) => {
    const suggestedDataFetcher = useFetcher(fetchSuggestedProfileData, {}, []);

    const { t } = useTranslation("session");

    const addRole = useCallback(
      (role: Partial<IConsultantProfileRole>) => {
        form.setFieldValue("roles", [...form.values.roles, role]);
      },
      [form],
    );

    const filterOption = (option: Option<IRole>, _: string) =>
      !value.find((r: IConsultantProfileRole) => r.role && r.role.id === option.data.id);

    const handleSuggestedRoleClick = useCallback(
      (role: IRole) => {
        const emptyRoleIndex = form.values.roles.findIndex((r: IConsultantProfileRole) => r.role === undefined);
        if (emptyRoleIndex > -1) {
          form.setFieldValue("roles", [
            ...form.values.roles.slice(0, emptyRoleIndex),
            { role },
            ...form.values.roles.slice(emptyRoleIndex + 1),
          ]);
          return;
        }

        addRole({ role });
      },
      [form],
    );

    const removeRole = useCallback(
      (role: IRole) => {
        if (form.values.roles.length === 1 && form.values.roles[0].role?.id === role?.id) {
          form.setFieldValue("roles", [{}]);
        } else {
          form.setFieldValue("roles", [
            ...form.values.roles.filter((r: IConsultantProfileRole) => r.role?.id !== role?.id),
          ]);
        }
      },
      [form],
    );

    const handleCheckedSuggestedRoleClick = useCallback(
      (role: IRole) => {
        removeRole(role);
      },
      [removeRole],
    );

    const handleDeleteRoleClick = useCallback(
      (index: number) => {
        form.setFieldValue("roles", [...form.values.roles.slice(0, index), ...form.values.roles.slice(index + 1)]);
      },
      [form],
    );

    const handleAddNextClick = useCallback(() => {
      addRole({ role: null as any });
    }, [addRole]);

    return (
      <Flex flexGrow={1} flexDirection="column" gap="sm">
        {showCompanyPosition && (
          <Flex flexGrow={1} flexDirection="column" gap="md">
            <FormGroup
              label={<FormLabel label={t("CONSULTANT_PROFILES.DETAIL.COMPANY_POSITION.LABEL")} />}
              error={
                <FormikError
                  name="position.name"
                  overrideMessages={{
                    [VALIDATION_TYPES.MAX_LENGTH]: t("common:VALIDATION_MESSAGES.MAX_LENGTH", {
                      value: POSITION_MAX_CHARACTERS,
                    }),
                  }}
                />
              }>
              <FormikTextInput
                placeholder={t("CONSULTANT_PROFILES.DETAIL.COMPANY_POSITION.PLACEHOLDER")}
                type="text"
                name="position.name"
                value={form.values.position.name}
                required
              />
            </FormGroup>
            <Flex justifyContent="space-between">
              <Header weight="bold">
                {t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.PROFILE.ROLE.MODAL.SECOND_TITLE_WITH_POSITION`)}
              </Header>
            </Flex>
          </Flex>
        )}

        <Flex flexGrow={1} flexDirection="column" gap="sm">
          <Flex flexGrow={1} flexDirection="column" gap="sm">
            {value.map((role, roleIndex) => (
              <Box key={role.role?.name || roleIndex}>
                <Grid gap={["xxs", null, "sm"]} columns={["1fr", null, "1fr 1fr"]}>
                  <Box flexGrow={1}>
                    <FormGroup label={<FormLabel label={t("CONSULTANT_PROFILES.DETAIL.ROLE.LABEL")} required />}>
                      <FormikRoleCreatableTypeahead
                        value={role.role}
                        name={`roles[${roleIndex}].role`}
                        testName="role"
                        valueKey="id"
                        labelKey="name"
                        filterOption={filterOption}
                        size="md"
                        placeholder={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.PLACEHOLDER_ROLE")}
                        required
                        form={form}
                      />
                    </FormGroup>
                  </Box>

                  <Flex flexGrow={1} flexDirection={"column"} gap={"xxs"}>
                    <FormLabel label={t("CONSULTANT_PROFILES.DETAIL.ROLE.SELECT_EXPERIENCE")} />
                    <Flex flexGrow={1} gap={"xs"}>
                      <Box flexGrow={1}>
                        <FormGroup>
                          <FormikCodelistGenericSelect
                            simpleValue
                            value={role.roleLevel}
                            name={`roles[${roleIndex}].roleLevel`}
                            testName="role-level"
                            query={ROLE_LEVELS_CODELIST_QUERY}
                            size="md"
                            placeholder={t(
                              "CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.PLACEHOLDER_LANGUAGE_LEVEL",
                            )}
                            isClearable={true}
                          />
                        </FormGroup>
                      </Box>
                      <Box>
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => handleDeleteRoleClick(roleIndex)}
                          iconLeft={<Icon color="grey2" icon="close" size="lg" />}
                        />
                      </Box>
                    </Flex>
                  </Flex>
                </Grid>
                <FormikError name={`roles[${roleIndex}].roleLevel`} />
              </Box>
            ))}
            <Flex>
              <Button type="button" variant="secondary" display="inline-block" onClick={handleAddNextClick}>
                <Text>{t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.BUTTON_ADD_NEXT_ROLE")}</Text>
              </Button>
            </Flex>
            {suggestedDataFetcher.payload && (
              <SuggestedSection header={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.SUGGESTED_ROLES")}>
                <SuggestedRolesList
                  onClick={handleSuggestedRoleClick}
                  onCheckedClick={handleCheckedSuggestedRoleClick}
                  suggestedRoles={suggestedDataFetcher.payload.roles}
                  selectedRoles={value?.map(r => r.role)}
                />
              </SuggestedSection>
            )}
          </Flex>
        </Flex>
      </Flex>
    );
  },
);

export const FormikConsultantProfileRoleSection =
  withFormikField<IConsultantProfileRoleSectionProps>(ConsultantProfileRoleSection);
